/* global BigInt */
const NUMERIC_FIELD_TYPES = new Set(["currency", "decimal", "number"]);
const OPERATIONS = new Set(["sum", "subtract", "multiply", "divide"]);
const MAX_DECIMAL_PLACES = 8;
const WORKING_DECIMAL_PLACES = 12;

export const REPORT_CALCULATION_OPERATION_OPTIONS = [
  {
    value: "sum",
    label: "Somar valores",
    description: "Soma todos os valores do campo nos registros filtrados.",
  },
  {
    value: "subtract",
    label: "Subtrair em sequência",
    description: "Usa o primeiro valor filtrado e subtrai os seguintes.",
  },
  {
    value: "multiply",
    label: "Multiplicar em sequência",
    description: "Multiplica todos os valores filtrados do campo.",
  },
  {
    value: "divide",
    label: "Dividir em sequência",
    description: "Usa o primeiro valor filtrado e divide pelos seguintes.",
  },
];

const operationLabel = (operation) =>
  REPORT_CALCULATION_OPERATION_OPTIONS.find((option) => option.value === operation)?.label ||
  "Calcular";

const normalizeFieldType = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeColumnSearchText = (value) =>
  typeof value === "string"
    ? value
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
    : "";

const isCurrencyReportColumn = (column) => {
  const fieldType = normalizeFieldType(column?.fieldType);
  if (fieldType === "currency") return true;
  if (!NUMERIC_FIELD_TYPES.has(fieldType)) return false;

  const descriptor = [column?.label, column?.fieldLabel, column?.fieldId]
    .map(normalizeColumnSearchText)
    .filter(Boolean)
    .join(" ");

  return /(^| )(valor|preco|custo|saldo|montante|receita|despesa|pagamento|mensalidade)( |$)/.test(
    descriptor
  );
};

const normalizeDecimalPlaces = (value, fallback = 2) => {
  const numeric = Number(value);
  if (!Number.isInteger(numeric)) return fallback;
  return Math.min(Math.max(numeric, 0), MAX_DECIMAL_PLACES);
};

export const isNumericReportColumn = (column) =>
  Boolean(
    column &&
      column.source === "form" &&
      NUMERIC_FIELD_TYPES.has(normalizeFieldType(column.fieldType))
  );

export const buildReportHeaders = (columns) =>
  (Array.isArray(columns) ? columns : []).map((column) => ({
    ...column,
    label:
      typeof column?.label === "string" && column.label.trim()
        ? column.label.trim()
        : column?.fieldId || "",
    description: "",
  }));

const defaultDecimalPlaces = (column, operation = "sum") => {
  const fieldType = normalizeFieldType(column?.fieldType);
  if (isCurrencyReportColumn(column)) return 2;
  if (fieldType === "number") return operation === "divide" ? 2 : 0;
  return normalizeDecimalPlaces(column?.decimalPlaces, 2);
};

export const buildDefaultReportCalculationLabel = (column, operation = "sum") => {
  const fieldLabel =
    typeof column?.label === "string" && column.label.trim()
      ? column.label.trim()
      : column?.fieldId || "campo";
  const prefix =
    operation === "subtract"
      ? "Diferença"
      : operation === "multiply"
      ? "Produto"
      : operation === "divide"
      ? "Divisão"
      : "Total";
  return `${prefix} de ${fieldLabel}`;
};

export const createReportCalculationDraft = (column, index = 0) => ({
  id: `calculo_${Date.now()}_${index}`,
  columnId: column?.id || "",
  operation: "sum",
  label: buildDefaultReportCalculationLabel(column, "sum"),
  decimalPlaces: defaultDecimalPlaces(column, "sum"),
});

export const normalizeReportCalculations = (calculations, columns) => {
  if (!Array.isArray(calculations) || !Array.isArray(columns)) return [];

  const numericColumns = new Map(
    columns.filter(isNumericReportColumn).map((column) => [column.id, column])
  );
  const seenIds = new Set();
  const normalized = [];

  calculations.slice(0, 20).forEach((entry, index) => {
    if (!entry || typeof entry !== "object") return;
    const columnId =
      typeof entry.columnId === "string" && entry.columnId.trim()
        ? entry.columnId.trim()
        : "";
    const column = numericColumns.get(columnId);
    if (!column) return;

    const operation = OPERATIONS.has(entry.operation) ? entry.operation : "sum";
    const rawId =
      typeof entry.id === "string" && entry.id.trim()
        ? entry.id.trim().slice(0, 80)
        : `calculo_${index + 1}_${column.fieldId}`;
    if (seenIds.has(rawId)) return;
    seenIds.add(rawId);

    const label =
      typeof entry.label === "string" && entry.label.trim()
        ? entry.label.trim().slice(0, 120)
        : buildDefaultReportCalculationLabel(column, operation);
    normalized.push({
      id: rawId,
      columnId,
      fieldId: column.fieldId,
      fieldLabel: column.label,
      fieldType: isCurrencyReportColumn(column)
        ? "currency"
        : normalizeFieldType(column.fieldType),
      operation,
      label,
      decimalPlaces: normalizeDecimalPlaces(
        entry.decimalPlaces,
        defaultDecimalPlaces(column, operation)
      ),
    });
  });

  return normalized;
};

const pow10 = (exponent) => 10n ** BigInt(Math.max(exponent, 0));

const divideRounded = (numerator, denominator) => {
  if (denominator === 0n) throw new Error("Divisão por zero");
  const negative = (numerator < 0n) !== (denominator < 0n);
  const absoluteNumerator = numerator < 0n ? -numerator : numerator;
  const absoluteDenominator = denominator < 0n ? -denominator : denominator;
  let quotient = absoluteNumerator / absoluteDenominator;
  const remainder = absoluteNumerator % absoluteDenominator;
  if (remainder * 2n >= absoluteDenominator) quotient += 1n;
  return negative ? -quotient : quotient;
};

const parseLocalizedDecimal = (rawValue, fieldType) => {
  if (rawValue === null || rawValue === undefined) return null;
  if (typeof rawValue === "number") {
    if (!Number.isFinite(rawValue)) return null;
    return parseLocalizedDecimal(String(rawValue), fieldType);
  }

  let text = String(rawValue).trim();
  if (!text || text === "-") return null;

  const negativeByParentheses = /^\(.*\)$/.test(text);
  const negativeBySign = text.includes("-");
  text = text.replace(/[^0-9.,]/g, "");
  if (!text || !/[0-9]/.test(text)) return null;

  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  let decimalSeparator = "";

  if (lastComma >= 0 && lastDot >= 0) {
    decimalSeparator = lastComma > lastDot ? "," : ".";
  } else if (lastComma >= 0) {
    decimalSeparator = ",";
  } else if (lastDot >= 0) {
    const dotCount = (text.match(/\./g) || []).length;
    const digitsAfter = text.length - lastDot - 1;
    const currencyThousands =
      normalizeFieldType(fieldType) === "currency" &&
      dotCount >= 1 &&
      digitsAfter === 3;
    decimalSeparator = currencyThousands ? "" : ".";
  }

  let integerPart = text;
  let decimalPart = "";
  if (decimalSeparator) {
    const separatorIndex = text.lastIndexOf(decimalSeparator);
    integerPart = text.slice(0, separatorIndex);
    decimalPart = text.slice(separatorIndex + 1);
  }

  const integerDigits = integerPart.replace(/\D/g, "") || "0";
  const decimalDigits = decimalPart.replace(/\D/g, "");
  const combined = `${integerDigits}${decimalDigits}`.replace(/^0+(?=\d)/, "") || "0";
  let units;
  try {
    units = BigInt(combined);
  } catch {
    return null;
  }
  if (negativeByParentheses || negativeBySign) units = -units;
  return { units, scale: decimalDigits.length };
};

const rescale = (decimal, targetScale) => {
  if (decimal.scale === targetScale) return decimal.units;
  if (decimal.scale < targetScale) {
    return decimal.units * pow10(targetScale - decimal.scale);
  }
  return divideRounded(decimal.units, pow10(decimal.scale - targetScale));
};

const formatUnits = (units, decimalPlaces, currency) => {
  const negative = units < 0n;
  const absolute = negative ? -units : units;
  const padded = absolute.toString().padStart(decimalPlaces + 1, "0");
  const integerRaw =
    decimalPlaces > 0 ? padded.slice(0, -decimalPlaces) || "0" : padded;
  const decimalRaw = decimalPlaces > 0 ? padded.slice(-decimalPlaces) : "";
  const groupedInteger = integerRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const numeric = decimalPlaces > 0 ? `${groupedInteger},${decimalRaw}` : groupedInteger;
  const signed = negative ? `-${numeric}` : numeric;
  return currency ? `R$ ${signed}` : signed;
};

const formatDateReportValue = (rawValue) => {
  const text = String(rawValue ?? "").trim();
  if (!text) return text;
  const isoMatch = text.match(/^(\d{4})[-/](\d{2})[-/](\d{2})(?:[T\s].*)?$/);
  if (!isoMatch) return text;
  return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
};

export const formatReportColumnValue = (rawValue, column) => {
  if (rawValue === null || rawValue === undefined) return "";

  const text = String(rawValue).trim();
  if (!text || text === "-") return text;
  if (normalizeFieldType(column?.fieldType) === "date") return formatDateReportValue(rawValue);
  if (!isCurrencyReportColumn(column)) return text;

  const parsed = parseLocalizedDecimal(rawValue, "currency");
  if (!parsed) {
    return /^R\$\s*/i.test(text) ? text : `R$ ${text}`;
  }

  const outputUnits = rescale(parsed, 2);
  return formatUnits(outputUnits, 2, true);
};

const aggregateValues = (values, operation, workingScale) => {
  const scaled = values.map((value) => rescale(value, workingScale));
  if (!scaled.length) return null;

  if (operation === "sum") {
    return scaled.reduce((total, current) => total + current, 0n);
  }
  if (operation === "subtract") {
    return scaled.slice(1).reduce((total, current) => total - current, scaled[0]);
  }
  if (operation === "multiply") {
    return scaled.slice(1).reduce(
      (total, current) => divideRounded(total * current, pow10(workingScale)),
      scaled[0]
    );
  }
  return scaled.slice(1).reduce((total, current) => {
    if (current === 0n) throw new Error("Divisão por zero encontrada nos registros filtrados.");
    return divideRounded(total * pow10(workingScale), current);
  }, scaled[0]);
};

export const buildReportCalculationResults = ({ instances, calculations }) => {
  const safeInstances = Array.isArray(instances) ? instances : [];
  const safeCalculations = Array.isArray(calculations) ? calculations : [];

  return safeCalculations.map((calculation) => {
    const calculationFieldType = isCurrencyReportColumn(calculation)
      ? "currency"
      : calculation.fieldType;
    const values = safeInstances
      .map((instance) => {
        const summary = Array.isArray(instance?.formSummary) ? instance.formSummary : [];
        const entry = summary.find((item) => item?.id === calculation.columnId);
        return parseLocalizedDecimal(entry?.value, calculationFieldType);
      })
      .filter(Boolean);

    if (!values.length) {
      return {
        ...calculation,
        valuesCount: 0,
        rawValue: null,
        formattedValue: "-",
        error: "",
      };
    }

    const outputScale = normalizeDecimalPlaces(
      calculation.decimalPlaces,
      defaultDecimalPlaces(calculation, calculation.operation)
    );
    const workingScale = Math.max(
      WORKING_DECIMAL_PLACES,
      outputScale,
      ...values.map((value) => Math.min(value.scale, WORKING_DECIMAL_PLACES))
    );

    try {
      const aggregated = aggregateValues(values, calculation.operation, workingScale);
      const outputUnits = rescale(
        { units: aggregated, scale: workingScale },
        outputScale
      );
      return {
        ...calculation,
        valuesCount: values.length,
        rawValue: outputUnits.toString(),
        formattedValue: formatUnits(
          outputUnits,
          outputScale,
          normalizeFieldType(calculationFieldType) === "currency"
        ),
        error: "",
      };
    } catch (error) {
      return {
        ...calculation,
        valuesCount: values.length,
        rawValue: null,
        formattedValue: "Não calculado",
        error:
          typeof error?.message === "string" && error.message
            ? error.message
            : `Não foi possível ${operationLabel(calculation.operation).toLowerCase()}.`,
      };
    }
  });
};
